import type React from 'react';
import { useCallback, useMemo } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useLocation } from 'react-router';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { SessionUser } from 'src/shared/session';

import { useCurrentAuthenticatedUser, useSessionUuid } from 'src/app/shared/hooks/session';
import { isError, isLoading } from 'src/app/shared/utils/async';

import { VANITY_ERROR_MAP } from '../../../../../constants';
import { parseError } from '../../../../../utils';
import { useVanityModalState } from '../../../../modalContext/context';
import { saveVanityUrl } from '../useConfirmVanityUrl/api';

type MutationArgs = {
	url: string;
	petitionSlug: string;
	petitionId: string;
	user: SessionUser | undefined;
	uuid: string;
	utilityContext: UtilityContext;
};

type UseConfirmVanityLinkReturn = ModelHookResult<
	{ loading: boolean; urlError: string[] | null; vanityUrl: string },
	{
		handleEdit: () => void;
		handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
	}
>;

type Props = {
	closeModal: ({ status, url }: { status: 'success'; url: string }) => void;
	petitionSlug: string;
	petitionId: string;
	displayedSignatures: number;
};

// eslint-disable-next-line max-lines-per-function
export function useConfirmVanityUrlForm({
	closeModal,
	petitionSlug,
	petitionId,
	displayedSignatures,
}: Props): UseConfirmVanityLinkReturn {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const { translate } = useI18n();
	const track = useTracking();
	const trackingData = {
		petition_id: `petition_${petitionId}`,
		num_signatures: displayedSignatures,
	};

	const [{ vanityUrl }, { setViewState }] = useVanityModalState();

	const {
		mutate: saveUrl,
		reset,
		...confirmUrlState
	} = useMutation({
		mutationFn: async ({ utilityContext, ...args }: MutationArgs) => saveVanityUrl(args, utilityContext),
		onSuccess: () => {
			void track('custom_url_confirm_submit_click', trackingData);
			closeModal({ status: 'success', url: vanityUrl });
		},
		onError: (error) => {
			void track('custom_url_confirm_error', trackingData);
			if (parseError(error).includes(VANITY_ERROR_MAP.UNAUTHENTICATED.code)) {
				const params = encodeURIComponent(`${pathname}${search}`);
				navigate(`/login_or_join/redirected?redirectTo=${params}`, { forceMode: 'external' });
			}
		},
	});

	const urlError = useMemo(() => {
		if (isError(confirmUrlState))
			return parseError(confirmUrlState.error).map((code) => translate(VANITY_ERROR_MAP[code].message));
		return null;
	}, [confirmUrlState, translate]);

	const loading = isLoading(confirmUrlState);

	const utilityContext = useUtilityContext();
	const user = useCurrentAuthenticatedUser();
	const uuid = useSessionUuid();
	const handleSubmit = useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			saveUrl({ url: vanityUrl, petitionSlug, petitionId, user, uuid, utilityContext });
		},
		[petitionId, petitionSlug, saveUrl, user, utilityContext, uuid, vanityUrl],
	);

	const handleEdit = useCallback(() => {
		setViewState('CREATE');
	}, [setViewState]);

	return { data: { loading, urlError, vanityUrl }, actions: { handleEdit, handleSubmit } };
}
